import { fabric } from "fabric";
import { DirectionEnum, ObjectSuperTypesEnum, ObjectTypesEnum } from "../../../enums";

export type RowLabelSide = DirectionEnum.Left | DirectionEnum.Right;

type GetRowLabelPlacementParams = {
  selectedItem: any;
  rowSeatSize: number;
  text: string;
  side: RowLabelSide;
  labelOffset: number;
  fontFamily?: string;
};

type CreateRowLabelParams = {
  selectedItem: any;
  rowSeatSize: number;
  text: string;
  side: RowLabelSide;
  labelOffset: number;
  angle: number;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  fontFamily?: string;
};

type UpdateExistingRowLabelParams = {
  selectedItem: any;
  label: any;
  rowSeatSize: number;
  text: string;
  side: RowLabelSide;
  labelOffset: number;
  angle: number;
  fontFamily?: string;
};

export class RowLabelHelper {
  static shouldFlipRowText(angle: number): boolean {
    const normalized = ((angle % 360) + 360) % 360;
    return normalized >= 90 && normalized < 270;
  }

  static getReadableAngle(angle: number): number {
    const normalized = ((angle % 360) + 360) % 360;
    const readable = normalized + (this.shouldFlipRowText(normalized) ? 180 : 0);
    return ((readable + 180) % 360) - 180;
  }

  // Recenter in the parent's coordinate system without baking ancestor transforms
  // into the children. Every seat keeps exactly the same canvas transform.
  static updateBounds(group: any) {
    const children = group.getObjects();
    if (!children.length) return;
    const points: fabric.Point[] = [];
    children.forEach((child: any) => {
      const matrix = child.calcTransformMatrix(true);
      const halfWidth = (child.width + (child.strokeWidth || 0)) / 2;
      const halfHeight = (child.height + (child.strokeWidth || 0)) / 2;
      [-halfWidth, halfWidth].forEach(x => [-halfHeight, halfHeight].forEach(y => {
        points.push(fabric.util.transformPoint(new fabric.Point(x, y), matrix));
      }));
    });
    const minX = Math.min(...points.map(point => point.x));
    const maxX = Math.max(...points.map(point => point.x));
    const minY = Math.min(...points.map(point => point.y));
    const maxY = Math.max(...points.map(point => point.y));
    const offset = new fabric.Point((minX + maxX) / 2, (minY + maxY) / 2);
    const center = fabric.util.transformPoint(offset, group.calcTransformMatrix(true));
    children.forEach((child: any) => {
      child.set({ left: child.left - offset.x, top: child.top - offset.y });
      child.setCoords();
    });
    group.set({ width: maxX - minX, height: maxY - minY, dirty: true });
    group.setPositionByOrigin(center, 'center', 'center');
    group.setCoords();
    if (group.group) this.updateBounds(group.group);
  }

  static addRowLabel(selectedItem: any, label: any) {
    const matrix = fabric.util.multiplyTransformMatrices(
      fabric.util.invertTransform(selectedItem.calcTransformMatrix()), label.calcTransformMatrix()
    );
    const transform = fabric.util.qrDecompose(matrix);
    label.set({
      angle: transform.angle, scaleX: transform.scaleX, scaleY: transform.scaleY,
      skewX: transform.skewX, skewY: 0, flipX: false, flipY: false,
    });
    label.setPositionByOrigin(new fabric.Point(transform.translateX, transform.translateY), 'center', 'center');
    selectedItem.add(label);
    this.updateBounds(selectedItem);
  }

  static getSeatObjects(selectedItem: any): any[] {
    return selectedItem
      .getObjects()
      .filter((obj: any) => obj.type === ObjectTypesEnum.Group && obj.superType === ObjectSuperTypesEnum.Seat);
  }

  static getTextboxes(selectedItem: any): any[] {
    return selectedItem.getObjects(ObjectTypesEnum.TextBox) || [];
  }

  static getRowLabelBySide(
    selectedItem: any,
    side: RowLabelSide
  ): any | undefined {
    return this.getTextboxes(selectedItem).find((t: any) => t.side === side);
  }

  static removeRowLabel(
    selectedItem: any,
    side: RowLabelSide
  ): any | undefined {
    const label = this.getRowLabelBySide(selectedItem, side);

    if (label) {
      selectedItem.remove(label);
      this.updateBounds(selectedItem);
    }

    return label;
  }

  static getRowLabelPlacement({
    selectedItem,
    rowSeatSize,
    text,
    side,
    labelOffset,
    fontFamily = "Roboto",
  }: GetRowLabelPlacementParams) {
    const seats = this.getSeatObjects(selectedItem);
    if (!seats.length) return null;

    const firstSeat = seats[0];
    const lastSeat = seats[seats.length - 1];

    // Usamos el centro REAL de cada asiento (getCenterPoint) en vez de
    // aproximarlo con `left + rowSeatSize/2`. Tras girar la fila, el texto de
    // cada asiento se contra-rota y cambia el bounding box de cada grupo de
    // asiento de forma distinta; `left + rowSeatSize/2` deja de coincidir con
    // el centro del círculo y produce huecos asimétricos entre izquierda y
    // derecha. getCenterPoint() devuelve el centro en coordenadas locales del
    // grupo de la fila, que es justo el marco que luego proyectamos.
    const firstCenter = firstSeat.getCenterPoint();
    const lastCenter = lastSeat.getCenterPoint();

    const dx = lastCenter.x - firstCenter.x;
    const dy = lastCenter.y - firstCenter.y;

    const distance = Math.hypot(dx, dy);
    if (!distance) return null;

    const unitX = dx / distance;
    const unitY = dy / distance;

    const previewText = new fabric.Text(text, {
      fontSize: rowSeatSize - 2,
      fontFamily,
      originX: "center",
      originY: "center",
    });

    const width = previewText.width || 0;

    const firstSeatCenterX = firstCenter.x;
    const firstSeatCenterY = firstCenter.y;

    const lastSeatCenterX = lastCenter.x;
    const lastSeatCenterY = lastCenter.y;

    // Distancia desde el CENTRO del asiento extremo hasta el CENTRO de la
    // etiqueta, medida a lo largo de la línea de la fila:
    //   rowSeatSize/2  -> del centro del asiento a su borde
    //   labelOffset    -> hueco deseado entre el borde del asiento y el borde
    //                     de la etiqueta (mismo valor a ambos lados)
    //   width/2        -> del borde de la etiqueta a su centro
    // Al ser idéntico para izquierda y derecha, la separación queda simétrica.
    const offset = rowSeatSize / 2 + labelOffset + width / 2;

    const localLeft =
      side === DirectionEnum.Left
        ? firstSeatCenterX - unitX * offset
        : lastSeatCenterX + unitX * offset;

    const localTop =
      side === DirectionEnum.Left
        ? firstSeatCenterY - unitY * offset
        : lastSeatCenterY + unitY * offset;

    // Corrección perpendicular (misma para ambos lados) para centrar
    // visualmente el texto sobre la línea de asientos.
    const fontSize = rowSeatSize - 2;
    const verticalCorrection = fontSize * 0.08;

    const correctedLeft = localLeft - unitY * verticalCorrection;
    const correctedTop = localTop + unitX * verticalCorrection;

    // El punto de canvas se deriva EXACTAMENTE del mismo punto local corregido,
    // para que el camino de creación (canvasLeft/canvasTop) y el de
    // actualización (localLeft/localTop) representen la misma posición y no
    // haya diferencia de separación entre izquierda y derecha.
    const groupMatrix = selectedItem.calcTransformMatrix();
    const canvasPoint = fabric.util.transformPoint(
      new fabric.Point(correctedLeft, correctedTop),
      groupMatrix
    );

    return {
      localLeft: correctedLeft,
      localTop: correctedTop,
      canvasLeft: canvasPoint.x,
      canvasTop: canvasPoint.y,
      unitX,
      unitY,
      width,
      firstSeat,
      lastSeat,
    };
  }

  static createRowLabel({
    selectedItem,
    rowSeatSize,
    text,
    side,
    labelOffset,
    angle,
    fill = "#000000",
    stroke = "#000000",
    strokeWidth = 0,
    fontFamily = "Roboto",
  }: CreateRowLabelParams) {
    const placement = this.getRowLabelPlacement({
      selectedItem,
      rowSeatSize,
      text,
      side,
      labelOffset,
      fontFamily,
    });

    if (!placement) return null;

    // Create in canvas coordinates; addRowLabel converts the label to the row's
    // local coordinates and refreshes bounds without moving existing children.
    const label = new fabric.Text(text, {
      fontSize: rowSeatSize - 2,
      left: placement.canvasLeft,
      top: placement.canvasTop,
      fill,
      stroke,
      strokeWidth,
      fontFamily,
      originX: "center",
      originY: "center",
      type: ObjectTypesEnum.TextBox,
      hasControls: false,
      hasBorders: false,
      strokeUniform: true,
      selectable: false,
      lockMovementX: true,
      lockMovementY: true,
      hoverCursor: "pointer",
      visible: true,
      centeredRotation: true,
      angle: this.getReadableAngle(angle || 0),
    }) as any;

    label.set({
      side,
      centeredRotation: true,
      isRowTextFlipped: this.shouldFlipRowText(angle || 0),
    });

    label.initDimensions();
    label.setCoords();

    return label;
  }

  static updateExistingRowLabel({
    selectedItem,
    label,
    rowSeatSize,
    text,
    side,
    labelOffset,
    angle,
    fontFamily = "Roboto",
  }: UpdateExistingRowLabelParams) {
    const placement = this.getRowLabelPlacement({
      selectedItem,
      rowSeatSize,
      text,
      side,
      labelOffset,
      fontFamily,
    });

    if (!placement) return null;

    label.set({
      text,
      left: placement.localLeft,
      top: placement.localTop,
      originX: "center",
      originY: "center",
      centeredRotation: true,
      angle,
      fontFamily,
    });

    label.setCoords();

    return label;
  }
}
